import type { Address, Extension, Identifier, Patient as FhirPatient } from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import moment from 'moment';
import { formatPhoneNumberSimple } from 'utils/utils';

import usaStates from '../../../data/States';
import { PatientBuilder } from '../../../fhir-sdoh';
import AddressBuilder from '../../../models/addressBuilder';
import type { PatientCDOModel } from '../../../models/patient';
import PatientCDO from '../../../models/patient';

/**
 * The flat form draft both callers pass.
 *
 * Built on `Partial<PatientCDOModel>` rather than the model itself, because the
 * runtime contract is genuinely partial and there is direct evidence for that:
 * `utils.spec.ts`'s `formDetailsWithAddress` fixture omits `org`, `id`, `age`,
 * `genderPrefix` and 13 other fields the model declares required, and the tests
 * built on it pass. The producers do not close the gap either — `patient/edit`
 * seeds state from the fully-populated `PatientCDO` but then replaces it
 * wholesale with `props.patient`, whose completeness this module cannot see.
 *
 * That partiality is load-bearing rather than cosmetic: `PreparePatientPayLoad`
 * reads `patientDetails.org.toUpperCase()` with no guard on the Add path, so an
 * absent `org` throws there. The spec does not catch it because both its cases
 * pass `'Update'`. See the `SUSPECTED DEFECT` note at that read.
 *
 * `external_referral_checkbox` and `mrnNumber` are named rather than left to the
 * index signature so the existing dotted reads keep working:
 * `noPropertyAccessFromIndexSignature` would otherwise force them to bracket
 * form, and rewriting `x?.k` as `x?.['k']` changes the emitted code rather than
 * only its types.
 *
 * The index signature carries the computed-key reads `ValidatePatient` performs
 * over `validateKeys`.
 */
export type PatientFormDetails = Partial<PatientCDOModel> & {
  external_referral_checkbox?: string[];
  mrnNumber?: string[];
} & Record<string, unknown>;

/**
 * The FHIR patient this function returns: `PatientBuilder.build`'s `Patient` on
 * the Add path, the caller's `patientOldDetails` mutated in place on Update.
 *
 * This is `fhir/r4`'s `Patient` widened only where this function writes members
 * that resource declares more narrowly — `communication`, `name` and `telecom`
 * are rebuilt here from form fields as plain object literals rather than as the
 * `PatientCommunication`/`HumanName`/`ContactPoint` shapes FHIR specifies, so
 * they are stated as `unknown[]`. No index signature: `Patient` is an interface
 * and so has none implicitly, and adding one here would assert that every string
 * key is present.
 */
export type PatientPayload = Omit<FhirPatient, 'communication' | 'name' | 'telecom'> & {
  // `| undefined` as well as `?:`: `Patient` declares these as
  // `T[] | undefined`, and under `exactOptionalPropertyTypes` a bare `?:` would
  // not accept the `undefined` arm when a whole `Patient` is assigned here.
  communication?: unknown[] | undefined;
  name?: unknown[] | undefined;
  telecom?: unknown[] | undefined;
};

// Edit/Save Patient Utils
export function PreparePatientPayLoad(
  patientDetails: PatientFormDetails,
  operation: string,
  practitionerName: string,
  displayDeliveryAddress: boolean,
  patientId: string,
  careteamStatus: string,
  /**
   * The stored FHIR patient, mutated in place and returned on the Update path.
   * `undefined` on the Add path, where it is never read — see the `SUSPECTED
   * DEFECT` note on the assignment below for what happens if Update is reached
   * without it.
   */
  patientOldDetails: PatientPayload | undefined
): PatientPayload {
  // Patient object created.
  //
  // The `PatientCDO` seed is dead: both branches below reassign `patientPayLoad`
  // before any read (Add -> `PatientBuilder.build`, Update -> `patientOldDetails`).
  // It is retained because removing it is a runtime edit, and asserted through
  // `unknown` because the two shapes genuinely do not overlap — `PatientCDOModel`
  // is the flat form model (`address: string`, `city: string[]`) while every use
  // here is FHIR-shaped (`address: Address[]`). That non-overlap is the point: a
  // read of `patientPayLoad` before either reassignment would see form fields
  // under FHIR names.
  let patientPayLoad: PatientPayload = PatientCDO as unknown as PatientPayload;
  let dob = patientDetails.dob;
  // Converting our date format to API supported format
  if (!(dob === undefined || dob === '')) {
    const rawDate = dob.trim().split('/');
    dob = rawDate[2] + '-' + rawDate[0] + '-' + rawDate[1];
  } else if (operation === 'Add') {
    //set default dob to 1/1/1990
    dob = moment('1990-01-01').format('YYYY-MM-DD');
  }
  if (operation === 'Add') {
    /*
     * SUSPECTED DEFECT (one fact, many sites) — the Add path dereferences every
     * form field without a guard.
     *
     * `firstName`, `lastName`, `middleName`, `homePhone`, `mobilePhone`, `email`,
     * `addressLine1`, `addressLine2`, `city`, `state`, `zip`, `country`,
     * `language` and `provider` are all read as `field[0]`, which throws when the
     * draft lacks that field entirely. The draft genuinely can lack them — see
     * `PatientFormDetails` above for the evidence — and the `AddressBuilder`
     * calls just below compound it with a second dereference, `field[0].trim()`.
     *
     * The `!` marks below are that unguarded read stated in the type system.
     * They erase to nothing, so the throw is preserved exactly; `?.` or `?? ''`
     * would each convert a crash into a silently wrong patient record.
     *
     * Two reads are deliberately left alone because they carry a REAL runtime
     * guard the original author wrote: `memberId?.[0]?.toUpperCase()` and
     * `email[0]?.toLowerCase()`.
     */
    patientPayLoad = PatientBuilder.build(
      /*
       * SUSPECTED DEFECT — `org` is read unguarded on the Add path.
       *
       * Every other field in this call is read defensively (`?.` or an index),
       * but `org` is dereferenced directly, so a draft without it throws here.
       * `utils.spec.ts`'s fixture demonstrates a draft that lacks `org`; it does
       * not trip this only because both its cases pass `'Update'`.
       *
       * `ValidatePatient` has the same unguarded read (`patientDetails.org.toUpperCase()`
       * in the memberId branch) and is called immediately AFTER this function in
       * `patient/edit`'s `validatePatientDetails`, so the validation that would
       * report a missing organisation cannot run — this throws first.
       *
       * `!` preserves that throw.
       */
      patientDetails.org!.toUpperCase(),
      // Cast, not `!`: the `?.` chain is a real guard the original author wrote
      // and is expected to yield `undefined`. `PatientBuilder.build` declares the
      // parameter `string` and guards it internally (`if (payermemberId)`), so
      // the mismatch is in the declaration, not at this call.
      patientDetails?.memberId?.[0]?.toUpperCase() as string,
      patientDetails.firstName![0]!,
      patientDetails.lastName![0]!,
      patientDetails.middleName![0]!,
      // `dob` is `string | undefined` on entry, but this is the Add branch and
      // the `else if (operation === 'Add')` above assigns the 1990-01-01 default
      // for exactly the undefined/empty case, so it is a string here.
      dob!,
      patientDetails.gender!,
      formatPhoneNumberSimple(patientDetails.homePhone![0]!),
      formatPhoneNumberSimple(patientDetails.mobilePhone![0]!),
      // Cast for the same reason as `memberId` above: `?.` here is deliberate.
      patientDetails.email![0]?.toLowerCase() as string,
      patientDetails.addressLine1![0]!,
      patientDetails.addressLine2![0]!,
      patientDetails.city![0]!,
      patientDetails.state![0]!,
      patientDetails.zip![0]!,
      patientDetails.country![0]!,
      patientDetails.language![0]!,
      [{ code: '', display: '' }],
      //[],
      practitionerName,
      patientDetails?.otherId?.[0],
      patientDetails?.mrnNumber?.[0],
      careteamStatus,
      patientDetails.provider![0],
      practitionerName,
      'NEW',
      patientDetails?.eligibilityGroup
    );
    let deliveryAddress: Address = {};
    // `!` throughout: `noUncheckedIndexedAccess` makes each `[0]` read
    // `string | undefined`, and these `.trim()` calls are unguarded today — an
    // empty array throws. The assertion preserves that throw; `?.` would convert
    // it into a silent `undefined` reaching the address builder.
    if (displayDeliveryAddress) {
      deliveryAddress = AddressBuilder(
        'physical',
        patientDetails.addressLine1![0]!.trim(),
        patientDetails.addressLine2![0]!.trim(),
        patientDetails.city![0]!.trim(),
        patientDetails.state![0]!.trim(),
        patientDetails.zip![0]!.trim(),
        patientDetails.country![0]!.trim()
      );
    } else {
      deliveryAddress = AddressBuilder(
        'physical',
        patientDetails.delivery_addressLine1![0] ? patientDetails.delivery_addressLine1![0].trim() : '',
        patientDetails.delivery_addressLine2![0]!.trim(),
        patientDetails.delivery_city![0]!.trim(),
        patientDetails.delivery_state![0]!.trim(),
        patientDetails.delivery_zip![0]!.trim(),
        patientDetails.delivery_country![0]!.trim()
      );
    }

    // `PatientBuilder.build` always emits an `address` array with one entry, so
    // both assertions hold on this path; they preserve the existing throw if it
    // ever stops doing so.
    patientPayLoad.address![0]!.type = 'postal';
    /*
     * SUSPECTED DEFECT — Add path writes a non-FHIR `gender` code, undoing the
     * builder's normalisation one statement after it ran.
     *
     * `fhir/r4` types `Patient.gender` as `'male' | 'female' | 'other' |
     * 'unknown' | undefined`, and `PatientBuilder.build` respects that:
     * `fhir-sdoh/common/fhir-builders/patient.ts:88` maps the form value with
     * `gender.toUpperCase() === 'MALE' ? 'male' : ... : 'unknown'`.
     *
     * This line then overwrites that normalised value with the raw form string.
     * `genderOptions` in `patient/edit/index.tsx` supplies 'Male' / 'Female' /
     * 'Unknown' (capitalised), so a patient created through the Add path is
     * persisted with `gender: 'Male'` rather than `'male'`, and with `''` when
     * the field is blank — `''` is not in the value set at all.
     *
     * The assertion below records the mismatch rather than correcting it: the
     * non-overlap between the form vocabulary and the FHIR value set IS the
     * finding, and normalising here would change what is written to the server.
     */
    patientPayLoad.gender = (!patientDetails.gender
      ? ''
      : patientDetails.gender.trim()) as FhirPatient['gender'];
    patientPayLoad.address!.push(deliveryAddress);

    //external referral default to false.
    //TODO check if patient has any other extensions
    patientPayLoad['extension'] = [
      {
        url: 'https://projectwell.io/fhir/extensions/external-referral',
        valueString: patientDetails?.external_referral_checkbox
          ? patientDetails?.external_referral_checkbox?.[0]
          : 'false',
      },
    ];
  } else {
    /*
     * SUSPECTED DEFECT — the Update path throws when the caller has no stored
     * patient to update.
     *
     * `patientOldDetails` is optional at this signature and `patient/edit`
     * supplies it as `props?.fhirPatient`, so it is `undefined` whenever that
     * prop is absent. Every statement from here down reads or writes members on
     * it (`patientPayLoad.birthDate` on the next line first), so `undefined`
     * produces a TypeError rather than falling back to the `PatientCDO` seed the
     * declaration suggests.
     *
     * The `!` states the assumption this code already makes without adding the
     * guard that would change the throw into a silent no-op.
     */
    //if update, we want to keep out old data
    patientPayLoad = patientOldDetails!;
    patientPayLoad.birthDate = patientPayLoad.birthDate === dob ? patientPayLoad.birthDate : dob;
    patientPayLoad.communication = [
      {
        language: {
          text: updatePatientData(patientPayLoad, 'communication.language.text', patientDetails?.language?.[0]),
        },
      },
    ];
    /*
     * SUSPECTED DEFECT — Update path writes the raw form `gender` with no
     * normalisation at all. Same value-set mismatch as the Add path above, but
     * here there is no builder to undo: whenever the form value differs from
     * what the stored patient carries, the capitalised form string ('Male') is
     * written straight onto `Patient.gender`.
     *
     * Note the comparison that guards it can never be equal in the changed case:
     * the stored value is lowercase FHIR ('male') and the form value is
     * capitalised ('Male'), so this branch takes the `patientDetails` arm on
     * every save even when the user did not touch the gender field.
     */
    patientPayLoad.gender = (
      patientPayLoad?.gender === patientDetails?.gender ? patientPayLoad?.gender : patientDetails?.gender
    ) as FhirPatient['gender'];
    patientPayLoad.telecom = [
      {
        system: 'phone',
        use: 'home',
        value: updatePatientData(
          patientPayLoad,
          "telecom.where(system='phone').where(use='home').value",
          formatPhoneNumberSimple(patientDetails?.homePhone?.[0])
        ),
      },
      {
        system: 'phone',
        use: 'mobile',
        value: updatePatientData(
          patientPayLoad,
          "telecom.where(system='phone').where(use='mobile').value",
          formatPhoneNumberSimple(patientDetails?.mobilePhone?.[0])
        ),
      },
      {
        system: 'email',
        value: updatePatientData(
          patientPayLoad,
          "telecom.where(system='email').value",
          patientDetails?.email?.[0]?.toLowerCase()
        ),
      },
    ];
    patientPayLoad.name = [
      {
        family: updatePatientData(patientPayLoad, 'name.family', patientDetails?.lastName?.[0]),
        given: [
          updatePatientData(patientPayLoad, 'name.given.first()', patientDetails?.firstName?.[0]),
          updatePatientData(patientPayLoad, 'name.given.last()', patientDetails?.middleName?.[0]),
        ],
      },
    ];
    if (
      fhirpath.evaluate(
        patientPayLoad,
        "identifier.where(system='https://projectwell.io/fhir/identifiers/last-modified-by').value"
      )?.[0] !== practitionerName
    ) {
      /*
       * SUSPECTED DEFECT — `identifier` is read unguarded here, unlike everywhere
       * else in this function.
       *
       * The other-id and member-id blocks further down both open with
       * `if (!patientPayLoad?.identifier) { patientPayLoad.identifier = []; ... }`,
       * creating the array when the stored patient has none. This block does not:
       * `patientPayLoad.identifier.findIndex(...)` throws for a patient with no
       * `identifier` member, before either of those blocks can create it.
       *
       * Reachable because `identifier` is optional on `fhir/r4`'s `Patient` and
       * this branch is entered whenever the stored last-modified-by value differs
       * from the current practitioner — including when it is absent entirely,
       * which is exactly the no-identifiers case.
       *
       * `!` preserves that throw; the `[index]` assertion is separate and sound —
       * `findIndex` returned a valid index under the `!== -1` guard.
       */
      const index = patientPayLoad.identifier!.findIndex(
        (obj: Identifier) => obj.system === 'https://projectwell.io/fhir/identifiers/last-modified-by'
      );
      if (index !== -1) {
        patientPayLoad.identifier![index]!.value = practitionerName;
      } else {
        patientPayLoad.identifier!.push({
          system: 'https://projectwell.io/fhir/identifiers/last-modified-by',
          value: practitionerName,
        });
      }
    }

    const physicalAddress = patientPayLoad?.address?.find((address: Address) => address?.type === 'physical');
    const postalAddress = patientPayLoad?.address?.find((address: Address) => address?.type === 'postal');

    /*
     * SUSPECTED DEFECT — the rebuilt addresses can carry `undefined` members.
     *
     * Every field comes from `updatePatientData`, which returns
     * `string | undefined`: `undefined` whenever the form field is absent AND the
     * stored resource has no value at that FHIRPath. `state` has a second source
     * of `undefined` — `reformatState` returns it for any state name that is
     * neither a 2-letter code nor an exact `data/States.ts` match.
     *
     * `fhir/r4`'s `Address` allows these members to be OMITTED, not to be present
     * with the value `undefined`, which is the distinction `exactOptionalPropertyTypes`
     * draws. So the object below is genuinely not an `Address`, and what is sent
     * to the server is e.g. `{"state": undefined}` — serialising to a dropped key
     * via `JSON.stringify`, but to an explicit null through some transports.
     *
     * The declared type states what this literal actually produces. The
     * assignment into `patientPayLoad.address` below asserts across the gap
     * rather than filling it, because filling it means inventing a default the
     * code does not have.
     */
    const newPostalAddress: {
      city: string | undefined;
      country: string | undefined;
      line: (string | undefined)[];
      postalCode: string | undefined;
      state: string | undefined;
      type: string;
    } = {
      city: updatePatientData(postalAddress, 'city', patientDetails?.city?.[0]),
      country: updatePatientData(postalAddress, 'country', patientDetails?.country?.[0]),
      line: [
        updatePatientData(postalAddress, 'line.first()', patientDetails?.addressLine1?.[0]),
        updatePatientData(postalAddress, 'line.last()', patientDetails?.addressLine2?.[0]),
      ],
      postalCode: updatePatientData(postalAddress, 'postalCode', patientDetails?.zip?.[0]),
      state: updatePatientData(postalAddress, 'state', reformatState(patientDetails?.state?.[0])),
      type: 'postal',
    };
    if (displayDeliveryAddress) {
      const newPhysicalAddress = { ...newPostalAddress };
      newPhysicalAddress.type = 'physical';
      // Asserted across the gap documented on `newPostalAddress`: these objects
      // may carry present-and-`undefined` members that `Address` does not admit.
      patientPayLoad.address = [newPostalAddress, newPhysicalAddress] as Address[];
    } else {
      patientPayLoad.address = [
        newPostalAddress,
        {
          city: updatePatientData(
            physicalAddress,
            'city',
            displayDeliveryAddress ? patientDetails?.city?.[0] : patientDetails?.delivery_city?.[0]
          ),
          country: updatePatientData(
            physicalAddress,
            'country',
            displayDeliveryAddress ? patientDetails?.country?.[0] : patientDetails?.delivery_country?.[0]
          ),
          line: [
            updatePatientData(
              physicalAddress,
              'line.first()',
              displayDeliveryAddress ? patientDetails?.addressLine1?.[0] : patientDetails?.delivery_addressLine1?.[0]
            ),
            updatePatientData(
              physicalAddress,
              'line.last()',
              displayDeliveryAddress ? patientDetails?.addressLine2?.[0] : patientDetails?.delivery_addressLine2?.[0]
            ),
          ],
          postalCode: updatePatientData(
            physicalAddress,
            'postalCode',
            displayDeliveryAddress ? patientDetails?.zip?.[0] : patientDetails?.delivery_zip?.[0]
          ),
          state: updatePatientData(
            physicalAddress,
            'state',
            displayDeliveryAddress
              ? reformatState(patientDetails?.state?.[0])
              : reformatState(patientDetails?.delivery_state?.[0])
          ),
          type: 'physical',
        },
        // Same gap as the branch above.
      ] as Address[];
    }

    if (
      patientDetails?.provider?.[0] &&
      patientPayLoad?.extension?.find((e: Extension) => e?.valueCoding?.code === 'PCP_ORGANIZATION')?.valueCoding?.display !==
        patientDetails?.provider?.[0]
    ) {
      if (!patientPayLoad?.extension) {
        patientPayLoad.extension = [];
        patientPayLoad.extension.push({
          url: 'https://projectwell.io/fhir/extensions',
          valueCoding: {
            code: 'PCP_ORGANIZATION',
            display: patientDetails?.provider?.[0],
            system: 'https://projectwell.io/fhir/extensions',
          },
        });
      } else {
        // The enclosing `else` narrows `extension` to present, so the optional
        // call above always runs and `index` is a number. `[index]` is asserted
        // under the `index >= 0` guard.
        const index = patientPayLoad?.extension?.findIndex((e: Extension) => e?.valueCoding?.code === 'PCP_ORGANIZATION');
        if (index >= 0) {
          if (!patientPayLoad.extension[index]!.valueCoding) {
            patientPayLoad.extension[index]!.valueCoding = {
              code: 'PCP_ORGANIZATION',
              display: patientDetails?.provider?.[0],
              system: 'https://projectwell.io/fhir/extensions',
            };
          } else {
            patientPayLoad.extension[index]!.valueCoding.display = patientDetails?.provider?.[0];
          }
        } else {
          patientPayLoad.extension.push({
            url: 'https://projectwell.io/fhir/extensions',
            valueCoding: {
              code: 'PCP_ORGANIZATION',
              display: patientDetails?.provider?.[0],
              system: 'https://projectwell.io/fhir/extensions',
            },
          });
        }
      }
    }
    if (
      // `undefined > 0` evaluates to `false`, which is the behaviour the `?.`
      // was written for. Cast rather than `!` so the optional chain keeps its
      // meaning — `no-non-null-asserted-optional-chain` is right that asserting
      // non-null on a chain designed to yield `undefined` states the opposite of
      // what this expression relies on.
      (patientDetails?.otherId?.length as number) > 0 &&
      patientPayLoad?.identifier?.find(
        (identifier: Identifier) => identifier?.system === 'https://projectwell.io/fhir/identifiers/other-id'
      )?.value !== patientDetails?.otherId?.[0]
    ) {
      if (!patientPayLoad?.identifier) {
        patientPayLoad.identifier = [];
        patientPayLoad.identifier.push({
          system: 'https://projectwell.io/fhir/identifiers/other-id',
          value: patientDetails?.otherId?.[0],
        });
      } else {
        /*
         * SUSPECTED DEFECT — no `-1` check, unlike the member-id block below.
         *
         * `findIndex` returns `-1` when the patient has identifiers but none with
         * the other-id system. The member-id block immediately below handles
         * exactly this case (`if (index < 0) { ...push... } else { ...assign... }`,
         * with a comment saying so); this block assigns unconditionally, so
         * `patientPayLoad.identifier[-1]` is `undefined` and reading `.value` off
         * it throws.
         *
         * Reachable whenever a patient carries any identifier but no other-id and
         * the form supplies one — the enclosing condition is satisfied precisely
         * then, because `find(...)?.value` is `undefined` and differs from the
         * supplied `otherId[0]`.
         *
         * `!` preserves the throw rather than guarding it.
         */
        const index = patientPayLoad?.identifier?.findIndex(
          (identifier: Identifier) => identifier?.system === 'https://projectwell.io/fhir/identifiers/other-id'
        );
        patientPayLoad.identifier[index]!.value = patientDetails?.otherId?.[0];
      }
    }

    if (
      // As above: `undefined > 0` is `false`.
      (patientDetails?.memberId?.length as number) > 0 &&
      patientDetails?.memberId?.[0] &&
      patientPayLoad?.identifier?.find(
        (identifier: Identifier) => identifier?.system === 'https://projectwell.io/fhir/identifiers/member-id'
      )?.value !== patientDetails?.memberId?.[0]
    ) {
      if (!patientPayLoad?.identifier) {
        patientPayLoad.identifier = [];
        patientPayLoad.identifier.push({
          system: 'https://projectwell.io/fhir/identifiers/member-id',
          value: patientDetails?.memberId?.[0],
        });
      } else {
        const index = patientPayLoad?.identifier?.findIndex(
          (identifier: Identifier) => identifier?.system === 'https://projectwell.io/fhir/identifiers/member-id'
        );
        //if no member-id identifier is found, create a new one, otherwise update the existing one
        // `index` is non-`undefined` here: the `else` arm means `identifier` is
        // present, so the optional call above ran. The `[index]` assertion holds
        // under the `index < 0` guard this block (unlike the other-id one) has.
        if (index < 0) {
          patientPayLoad.identifier.push({
            system: 'https://projectwell.io/fhir/identifiers/member-id',
            value: patientDetails?.memberId?.[0],
          });
        } else {
          patientPayLoad.identifier[index]!.value = patientDetails?.memberId?.[0];
        }
      }
    }

    //external referral default to false. If no value skip
    const external_referral_ext: { url: string; valueString: string | undefined } = {
      url: 'https://projectwell.io/fhir/extensions/external-referral',
      valueString: patientDetails?.external_referral_checkbox?.[0]
        ? patientDetails?.external_referral_checkbox?.[0]
        : 'false',
    };
    /* eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type */
    patientPayLoad?.extension
      ? (patientPayLoad.extension = updateExtensionValue(
          patientPayLoad.extension,
          external_referral_ext.url,
          external_referral_ext.valueString
        ))
      : (patientPayLoad['extension'] = [external_referral_ext]);
  }
  return patientPayLoad;
}
/**
 * Returns `ext` with the entry at `extURL` given `extVal`, appending one when no
 * entry carries that url. The input is never mutated.
 *
 * `Extension` comes from the sole caller, which passes `patientPayLoad.extension`.
 * The result is a union because the appended `newExt` is a bare
 * `{ url, valueString }` rather than a full `Extension` — `valueString` is
 * `string | undefined` here, which `Extension` does not admit as a present value
 * under `exactOptionalPropertyTypes`.
 */
const updateExtensionValue = (
  ext: Extension[],
  extURL: string,
  extVal: string | undefined
): (Extension | { url: string; valueString: string | undefined })[] => {
  let updatedExtArr, oldExt;
  const newExt: { url: string; valueString: string | undefined } = { url: extURL, valueString: extVal };
  /* eslint-disable-next-line prefer-const -- splitting the declaration reorders emitted statements */
  oldExt = ext.find((e) => e.url === extURL);
  if (!oldExt) {
    updatedExtArr = [...ext, newExt];
  } else {
    updatedExtArr = ext.map((e) => {
      if (e.url === extURL) {
        return { ...e, valueString: extVal };
      }
      return e;
    });
  }
  /* eslint-disable-next-line @typescript-eslint/no-unsafe-return -- the value is a `fhir-sdoh` export whose module ships no usable types */
  return updatedExtArr;
};
/**
 * The outcome of form validation. `errors` is the full list shown in the modal;
 * `patientBannerErrors` is the shorter field-name list the patient banner renders.
 */
export interface PatientValidations {
  valid: boolean;
  errors: string[];
  patientBannerErrors: string[];
}

export function ValidatePatient(
  patientDetails: PatientFormDetails,
  operation: string,
  practitionerName: string,
  displayDeliveryAddress: boolean,
  _patientId: string
): PatientValidations {
  const validations: { valid: boolean; errors: string[]; patientBannerErrors: string[] } = {
    valid: true,
    errors: [],
    patientBannerErrors: [],
  };

  const validateKeys = [
    { key: 'firstName', value: 'First name' },
    { key: 'lastName', value: 'Last name' },
    { key: 'memberId', value: 'Member Id' },
    { key: 'org', value: 'Organization' },
    { key: 'homePhone', value: 'Home phone' },
    { key: 'mobilePhone', value: 'Mobile phone' },
    { key: 'email', value: 'Email' }, // Not a mandatory field anymore.
    { key: 'addressLine1', value: 'Address' },
    { key: 'city', value: 'City' },
    { key: 'state', value: 'State' },
  ];

  //let validPhoneNumber = false;
  let mandatoryPhoneNumber = false;

  if (!displayDeliveryAddress) {
    validateKeys.push({
      key: 'delivery_addressLine1',
      value: 'Delivery Address',
    });
    validateKeys.push({
      key: 'delivery_city',
      value: 'Delivery Address - City',
    });
    validateKeys.push({
      key: 'delivery_state',
      value: 'Delivery Address - State',
    });
  }
  validateKeys.forEach((validateObj) => {
    //member id is not a required field for LYNX-OTC
    if (validateObj.key === 'memberId' && patientDetails[validateObj.key]?.[0] === undefined) {
      // SUSPECTED DEFECT — second unguarded `org` read; see the note at the
      // `PatientBuilder.build` call in `PreparePatientPayLoad`. A draft without
      // `org` throws here too, so the "Member Id is a mandatory field" message
      // this branch exists to produce is unreachable for such a draft.
      if (patientDetails.org!.toUpperCase() !== 'LYNX-OTC') {
        validations.valid = false;
        validations.errors.push(validateObj.value + ' is a mandatory field');
        validations.patientBannerErrors.push(validateObj.value);
      }
    } else if (
      /* eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- the receiver is a `fhir-sdoh` export whose module ships no usable types */
      (patientDetails[validateObj.key]?.[0] === undefined || patientDetails[validateObj.key]?.[0]?.trim() === '') &&
      !(
        validateObj.key === 'homePhone' ||
        validateObj.key === 'mobilePhone' ||
        validateObj.key === 'email' ||
        validateObj.key === 'memberId'
      )
    ) {
      validations.valid = false;
      validations.errors.push(validateObj.value + ' is a mandatory field');
      validations.patientBannerErrors.push(validateObj.value);
    } else if (
      (validateObj.key === 'homePhone' || validateObj.key === 'mobilePhone') &&
      !mandatoryPhoneNumber &&
      (patientDetails?.['homePhone']?.[0] === undefined || patientDetails?.['homePhone']?.[0]?.trim() === '') &&
      (patientDetails?.['mobilePhone']?.[0] === undefined || patientDetails?.['mobilePhone']?.[0]?.trim() === '')
    ) {
      validations.valid = false;
      mandatoryPhoneNumber = true;
      validations.errors.push('Enter either a Home phone # or a mobile phone #');
      validations.patientBannerErrors.push('Home phone # or mobile phone #');
    } else if (validateObj.key === 'email') {
       
      if (
        patientDetails?.email?.[0] !== undefined &&
        patientDetails?.email?.[0]?.trim() !== '' &&
        // The `\.` inside the two character classes is redundant, but un-escaping it
        // rewrites the regex literal — a change to the emitted program, not its types.
        // eslint-disable-next-line no-useless-escape
        !/^\w+([+\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(patientDetails?.email?.[0]?.trim())
      ) {
        validations.valid = false;
        validations.errors.push('Enter valid email address');
      }
    } else if (
      (validateObj.key === 'homePhone' || validateObj.key === 'mobilePhone') &&
      patientDetails[validateObj.key]?.[0] !== undefined &&
      patientDetails[validateObj.key]?.[0]?.trim() !== ''
    ) {
      // Phone format validation, Format: XXX-XXX-XXXX
      const PhoneValidationRegex2 = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
      // The enclosing condition already established this element is a non-empty
      // string; the cast states that rather than re-testing it.
      if (!PhoneValidationRegex2.test(patientDetails[validateObj.key]?.[0] as string)) {
        validations.valid = false;
        validations.errors.push('Not a valid ' + validateObj.value);
      }
    }
  });

  if (patientDetails.gender === '') {
    validations.valid = false;
    validations.errors.push('Gender is a mandatory field');
  }

  if (patientDetails.dob === 'undefined/undefined/undefined') {
    validations.valid = false;
    validations.errors.push('Date Of Birth is a mandatory field');
    validations.patientBannerErrors.push('Date Of Birth');
  }

  return validations;
}

/*
 * SUSPECTED DEFECT — the guard is always true and the wrong operator, and this
 * export has no consumers.
 *
 * `searchState !== undefined || searchState.trim() !== ''` uses `||` where the
 * intent was plainly `&&`: the first operand is true for every non-undefined
 * input, so the second is never evaluated and the empty-string case the author
 * meant to exclude falls through into the body. Because the parameter is now
 * typed `string`, TypeScript narrows `searchState` to `never` in the unreachable
 * right-hand operand — that `never` is the symptom, not the defect.
 *
 * Also note the lookup is inverted relative to `reformatState` above: this
 * matches the input against `state.key` (the abbreviation) and returns
 * `state.value` (the full name), i.e. abbreviation -> name, while `reformatState`
 * goes name -> abbreviation.
 *
 * Left as-is: a repo-wide search finds no importer, so correcting the operator
 * would change nothing today but would change this function's contract for any
 * future caller. The return type is `string | undefined` because the implicit
 * fall-through past the `if` returns `undefined`.
 */
export function FetchStateAbbr(searchState: string): string | undefined {
  // `searchState` narrows to `never` on the right operand precisely because the
  // `||` can never reach it — the defect described above. The cast keeps the dead
  // operand in the source rather than deleting it and losing the evidence.
  if (searchState !== undefined || (searchState as string).trim() !== '') {
    const fullState = usaStates.list.filter((state) => searchState.trim() === state.key);
    if (fullState.length) return fullState[0]!.value;
    else return searchState.trim();
  }
}

/**
 * Reads `dataName` off `source` and returns `newValue` unless the two already
 * agree.
 *
 * `source` is the union of what the callers actually pass, not one of them: the
 * `line.*` and address-field cases receive an `Address` (`postalAddress` /
 * `physicalAddress`), while the `communication.*`, `telecom.*`, `name.*` and
 * `identifier.*` cases receive the whole `PatientPayload`. The two groups are
 * disjoint, which is what the in-line note on the `fhirpath.evaluate` default
 * branch is describing — the FHIRPath expressions there are rooted at the
 * resource, so passing an `Address` makes them evaluate to nothing.
 */
function updatePatientData(
  patientPayLoad: Address | PatientPayload | undefined,
  dataName: string,
  newPatientPayload: string | undefined
): string | undefined {
  if (!patientPayLoad) return newPatientPayload ?? '';

  //compare the patient payload with updated data, if it's not the same, update it
  // `unknown[]` is what the local `fhirpath` shim declares `evaluate` returns;
  // the `Array.isArray` collapse below turns that arm into a string before any
  // return, which is what makes the declared `string | undefined` hold.
  let originalValue: string | unknown[] | undefined;

  // `line` is declared on the `Address` arm only, but these two cases are
  // reached solely from the address callers (`dataName` partitions the union at
  // the call sites). The assertions state that inline — a local binding would
  // add a statement to the emitted output, which a type-only change must not.
  switch (dataName) {
    case 'line.first()':
      originalValue = (patientPayLoad as Address).line?.[0];
      break;

    case 'line.last()':
      originalValue = (patientPayLoad as Address).line?.[1] ?? (patientPayLoad as Address).line?.[0];
      break;

    default:
      originalValue = fhirpath.evaluate(patientPayLoad, dataName); // should pass full resource for fhirpath.evaluate, such as the patientpayload, instead of patientpayload.address, otherwise will likely to return undefined and cause issue
  }
  if (Array.isArray(originalValue)) {
    originalValue = originalValue.toString();
  }
  return originalValue === newPatientPayload ? originalValue : newPatientPayload;
}

/*
 * SUSPECTED DEFECT — an unrecognised state name yields `undefined`, not a string.
 *
 * `usaStates.list.find(...)` returns `undefined` when no entry's `text` matches
 * `originalState`, so `selectedKey` is `string | undefined` and the assignment
 * below puts `undefined` into a variable the signature promises is a `string`.
 * The value flows out through `updatePatientData` into `Address.state` on both
 * the postal and physical addresses built in `PreparePatientPayLoad`.
 *
 * It is reachable: the match is against the full state name (`text`), so any
 * value that is neither a 2-letter abbreviation nor an exact full-name match —
 * a lowercase name, a name with trailing punctuation, a territory absent from
 * `data/States.ts` — takes this branch. The return type states what the function
 * actually produces rather than what the `let stateAbbreviation = ''` seed
 * suggests.
 */
//return 2 letter state abbreviation for the given state, if the input is already an abbreviation, return it directly
// `originalState` admits `undefined` because every caller passes an array index
// read (`patientDetails.state?.[0]`) and the first line already handles a falsy
// input by returning it — the previous `string` annotation described neither.
function reformatState(originalState: string | undefined): string | undefined {
  if (!originalState) return originalState ?? '';
  let stateAbbreviation: string | undefined = '';
  if (originalState?.length === 2) {
    stateAbbreviation = originalState;
  } else {
    const selectedOption = usaStates.list.find((opt) => opt.text === originalState); //find the corresponding state abbreviation based on the state full name
    const selectedKey = selectedOption?.key; //save state as 2 letter abbreviation in the backend
    stateAbbreviation = selectedKey;
  }

  return stateAbbreviation;
}
